import { useMemo, useState, type JSX, type ReactNode } from "react";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterChip,
  Inline,
  LoadingState,
  Stack,
  StatePill,
  type DataTableColumn,
} from "@genius-tracker/design-system";
import { planNameKey, type PlanId } from "@genius-tracker/i18n";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";

import { BUILT_IN_ROLES, permissionDescKey, roleLabelKey } from "../../../access/roles.js";
import { groupKeys } from "../../../entitlements/feature-catalog.js";
import { ApiError, apiGet } from "../../../lib/api.js";
import { useGtccHeadTabs } from "../control/HeadTabsContext.js";

interface OverrideRow {
  readonly key: string;
  readonly granted: boolean;
  readonly quotaMax: number | null;
}

interface ResolvedEntitlementsView {
  readonly plan: PlanId;
  readonly planDefaults: ReadonlyArray<string>;
  readonly overrides: ReadonlyArray<OverrideRow>;
  readonly resolved: {
    readonly plan: string;
    readonly entries: ReadonlyArray<string>;
    readonly quotas: Readonly<Record<string, number>>;
  };
}

/** `GET /v1/admin/entitlements/catalog` — A-ENT-P2, tenant-free catalog. */
interface FeatureCatalogEntry {
  readonly key: string;
  readonly group: string;
  readonly labelKey: string;
  readonly planDefault: ReadonlyArray<string>;
  readonly universal: boolean;
}

interface FeatureCatalogView {
  readonly groups: ReadonlyArray<string>;
  readonly catalog: ReadonlyArray<FeatureCatalogEntry>;
}

function SectionHeading({ children }: { readonly children: ReactNode }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: "var(--gt-sys-font-size-md)",
        fontWeight: "var(--gt-sys-font-weight-semibold)",
      }}
    >
      {children}
    </h3>
  );
}

function RolesCatalogCard(): JSX.Element {
  const { t } = useLocale();
  const roles = Object.values(BUILT_IN_ROLES);

  return (
    <Card
      elevation={1}
      data-testid="gtcc-cat-roles-card"
      style={{ minInlineSize: 0, blockSize: "100%" }}
    >
      <Stack gap={3}>
        <Stack gap={1}>
          <SectionHeading>{t("admin.roles.title")}</SectionHeading>
          <span
            style={{
              color: "var(--gt-sys-text-muted)",
              fontSize: "var(--gt-sys-font-size-sm)",
            }}
          >
            {t("admin.roles.subtitle")}
          </span>
        </Stack>

        <Stack gap={3}>
          {roles.map((role) => (
            <section key={role.id} data-testid={`gtcc-cat-role-${role.id}`}>
              <Stack gap={1}>
                <strong>{t(roleLabelKey(role.id))}</strong>
                <code
                  style={{
                    color: "var(--gt-sys-text-muted)",
                    fontSize: "var(--gt-sys-font-size-xs)",
                  }}
                >
                  {role.id}
                </code>
                <span
                  style={{
                    color: "var(--gt-sys-text-muted)",
                    fontSize: "var(--gt-sys-font-size-xs)",
                  }}
                >
                  {t("admin.roles.granted_count", { count: role.permissions.length })}
                </span>
                <ul
                  style={{
                    margin: 0,
                    paddingInlineStart: "var(--gt-sys-space-5)",
                    fontSize: "var(--gt-sys-font-size-sm)",
                  }}
                >
                  {role.permissions.map((permission) => (
                    <li key={permission}>
                      <code style={{ color: "var(--gt-sys-text-muted)" }}>{permission}</code>
                      {" — "}
                      {t(permissionDescKey(permission))}
                    </li>
                  ))}
                </ul>
              </Stack>
            </section>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function EntitlementsCatalogCard(): JSX.Element {
  const { t } = useLocale();
  const query = useQuery<ResolvedEntitlementsView, Error>({
    queryKey: ["gtcc", "cat", "entitlements"],
    queryFn: ({ signal }) =>
      apiGet<ResolvedEntitlementsView>("/v1/admin/entitlements", undefined, signal),
    staleTime: 30_000,
  });

  const forbidden = query.error instanceof ApiError && query.error.status === 403;
  const requestId = query.error instanceof ApiError ? query.error.requestId : undefined;
  // A successful but empty response is still a valid read state.  Keep the
  // catalogue card renderable while the API has no resolved entitlement
  // payload, rather than letting a missing nested object escape into the
  // view boundary.
  const groups = query.data ? groupKeys(query.data.resolved?.entries ?? []) : [];

  return (
    <Card
      elevation={1}
      data-testid="gtcc-cat-entitlements-card"
      style={{ minInlineSize: 0, blockSize: "100%" }}
    >
      {/* section: gibt Retry & Co. den Kartentitel als Kontext (Accessible-Name-Inventar). */}
      <section>
        <Stack gap={3}>
          <Stack gap={1}>
            <SectionHeading>{t("admin.entitlements.title")}</SectionHeading>
            <span
              style={{
                color: "var(--gt-sys-text-muted)",
                fontSize: "var(--gt-sys-font-size-sm)",
              }}
            >
              {t("admin.gtcc.cat.entitlements.subtitle")}
            </span>
          </Stack>

          {query.isPending ? <LoadingState label={t("admin.entitlements.loading")} /> : null}

          {query.isError ? (
            <ErrorState
              title={
                forbidden
                  ? t("admin.entitlements.forbidden.title")
                  : t("admin.entitlements.error.title")
              }
              body={
                <Stack gap={1}>
                  <span>
                    {forbidden
                      ? t("admin.gtcc.cat.entitlements.forbidden.body")
                      : t("admin.gtcc.cat.entitlements.error.body")}
                  </span>
                  {requestId ? (
                    <code>{t("common.error.request_id", { id: requestId })}</code>
                  ) : null}
                </Stack>
              }
              severity={forbidden ? "warn" : "error"}
              action={
                forbidden ? undefined : (
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    {t("common.retry")}
                  </Button>
                )
              }
            />
          ) : null}

          {query.isSuccess ? (
            <Stack gap={3} data-testid="gtcc-cat-entitlements-content">
              <Stack gap={1}>
                <strong>{t("admin.entitlements.plan.label")}</strong>
                <strong>{t(planNameKey(query.data.plan))}</strong>
              </Stack>

              {groups.length === 0 ? (
                <EmptyState
                  title={t("admin.gtcc.cat.entitlements.empty.title")}
                  body={t("admin.gtcc.cat.entitlements.empty.body")}
                />
              ) : (
                <Stack gap={3}>
                  {groups.map(({ group, keys }) => (
                    <section key={group.id} data-testid={`gtcc-cat-entitlement-group-${group.id}`}>
                      <Stack gap={1}>
                        <strong>{t(group.labelKey)}</strong>
                        <ul
                          style={{
                            margin: 0,
                            paddingInlineStart: "var(--gt-sys-space-5)",
                            fontSize: "var(--gt-sys-font-size-sm)",
                          }}
                        >
                          {keys.map((key) => (
                            <li key={key}>
                              <code>{key}</code>
                              {query.data.resolved.quotas[key] !== undefined ? (
                                <span style={{ color: "var(--gt-sys-text-muted)" }}>
                                  {" — "}
                                  {t("admin.entitlements.resolved.quota_suffix", {
                                    quota: query.data.resolved.quotas[key],
                                  })}
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </Stack>
                    </section>
                  ))}
                </Stack>
              )}
            </Stack>
          ) : null}
        </Stack>
      </section>
    </Card>
  );
}

/**
 * A-ENT-P2 — the full feature catalog (every key with category and plan
 * defaults), independent of any tenant, filterable by category. Reads
 * `GET /v1/admin/entitlements/catalog`
 * (`services/api/src/modules/admin/tenant-entitlements.routes.ts`); what a
 * tenant actually has is the card above, what a tenant *can* get is this one.
 */
function FeaturesCatalogCard(): JSX.Element {
  const { t } = useLocale();
  const [group, setGroup] = useState<string>("all");
  const query = useQuery<FeatureCatalogView, Error>({
    queryKey: ["gtcc", "cat", "features"],
    queryFn: ({ signal }) =>
      apiGet<FeatureCatalogView>("/v1/admin/entitlements/catalog", undefined, signal),
    staleTime: 30_000,
  });

  const forbidden = query.error instanceof ApiError && query.error.status === 403;
  const requestId = query.error instanceof ApiError ? query.error.requestId : undefined;
  const catalog = useMemo(
    () => (Array.isArray(query.data?.catalog) ? query.data.catalog : []),
    [query.data],
  );
  const groups = useMemo(() => {
    const declared = Array.isArray(query.data?.groups) ? query.data.groups : [];
    // Only categories with at least one feature make a useful chip.
    return declared.filter((id) => catalog.some((entry) => entry.group === id));
  }, [query.data, catalog]);
  const rows = group === "all" ? catalog : catalog.filter((entry) => entry.group === group);

  const columns: ReadonlyArray<DataTableColumn<FeatureCatalogEntry>> = [
    {
      id: "feature",
      header: t("admin.gtcc.cat.features.column.feature"),
      cell: (row) => t(`admin.entitlements.key.${row.key}`),
    },
    {
      id: "key",
      header: t("admin.gtcc.cat.features.column.key"),
      cell: (row) => <code>{row.key}</code>,
    },
    {
      id: "category",
      header: t("admin.gtcc.cat.features.column.category"),
      cell: (row) => t(`admin.entitlements.group.${row.group}`),
    },
    {
      id: "plans",
      header: t("admin.gtcc.cat.features.column.plans"),
      cell: (row) =>
        row.universal ? (
          <StatePill tone="success" label={t("admin.gtcc.cat.features.universal")} />
        ) : row.planDefault.length === 0 ? (
          <span style={{ color: "var(--gt-sys-text-muted)" }}>
            {t("admin.gtcc.cat.features.plans.none")}
          </span>
        ) : (
          <span>{row.planDefault.map((p) => t(planNameKey(p as PlanId))).join(", ")}</span>
        ),
    },
  ];

  return (
    <Card
      elevation={1}
      data-testid="gtcc-cat-features-card"
      style={{ minInlineSize: 0, blockSize: "100%", gridColumn: "1 / -1" }}
    >
      {/* section: gibt Retry & Co. den Kartentitel als Kontext (Accessible-Name-Inventar). */}
      <section>
        <Stack gap={3}>
          <Stack gap={1}>
            <SectionHeading>{t("admin.gtcc.cat.features.title")}</SectionHeading>
            <span
              style={{
                color: "var(--gt-sys-text-muted)",
                fontSize: "var(--gt-sys-font-size-sm)",
              }}
            >
              {t("admin.gtcc.cat.features.subtitle")}
            </span>
          </Stack>

          {query.isPending ? <LoadingState label={t("admin.gtcc.cat.features.loading")} /> : null}

          {query.isError ? (
            <ErrorState
              title={
                forbidden
                  ? t("admin.entitlements.forbidden.title")
                  : t("admin.entitlements.error.title")
              }
              body={
                <Stack gap={1}>
                  <span>
                    {forbidden
                      ? t("admin.gtcc.cat.features.forbidden.body")
                      : t("admin.gtcc.cat.features.error.body")}
                  </span>
                  {requestId ? (
                    <code>{t("common.error.request_id", { id: requestId })}</code>
                  ) : null}
                </Stack>
              }
              severity={forbidden ? "warn" : "error"}
              action={
                forbidden ? undefined : (
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    {t("common.retry")}
                  </Button>
                )
              }
            />
          ) : null}

          {query.isSuccess ? (
            <Stack gap={3} data-testid="gtcc-cat-features-content">
              {catalog.length === 0 ? (
                <EmptyState
                  title={t("admin.gtcc.cat.features.empty.title")}
                  body={t("admin.gtcc.cat.features.empty.body")}
                />
              ) : (
                <>
                  <Inline gap={2} wrap aria-label={t("admin.gtcc.cat.features.filter.label")}>
                    <FilterChip
                      active={group === "all"}
                      onClick={() => setGroup("all")}
                      testId="gtcc-cat-features-filter-all"
                    >
                      {t("admin.gtcc.cat.features.filter.all")}
                    </FilterChip>
                    {groups.map((id) => (
                      <FilterChip
                        key={id}
                        active={group === id}
                        onClick={() => setGroup(id)}
                        testId={`gtcc-cat-features-filter-${id}`}
                      >
                        {t(`admin.entitlements.group.${id}`)}
                      </FilterChip>
                    ))}
                  </Inline>
                  <span
                    data-testid="gtcc-cat-features-count"
                    style={{
                      color: "var(--gt-sys-text-muted)",
                      fontSize: "var(--gt-sys-font-size-sm)",
                    }}
                  >
                    {t("admin.gtcc.cat.features.count", {
                      count: rows.length,
                      total: catalog.length,
                    })}
                  </span>
                  <DataTable
                    caption={t("admin.gtcc.cat.features.subtitle")}
                    columns={columns}
                    rows={rows}
                    getRowId={(row) => row.key}
                    testId="gtcc-cat-features-table"
                    empty={{ title: t("admin.gtcc.cat.features.empty.title") }}
                  />
                </>
              )}
            </Stack>
          ) : null}
        </Stack>
      </section>
    </Card>
  );
}

export function GtccCatalogView(): JSX.Element {
  const { t } = useLocale();
  const headTabs = useGtccHeadTabs();

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.cat.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.cat.subtitle")}
        </p>
      </Stack>

      {headTabs}

      <div
        data-testid="gtcc-cat-card-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 22rem), 1fr))",
          gap: "var(--gt-sys-space-4)",
          alignItems: "stretch",
        }}
      >
        <RolesCatalogCard />
        <EntitlementsCatalogCard />
        <FeaturesCatalogCard />
      </div>

      <span
        style={{
          color: "var(--gt-sys-text-muted)",
          fontSize: "var(--gt-sys-font-size-xs)",
        }}
      >
        {t("admin.gtcc.cat.read_only_note")}
      </span>
    </Stack>
  );
}
